/**
 * Pure input mapping (no DOM): key codes → actions, camera-relative movement,
 * tab targeting and cast command construction. Everything here is unit-tested.
 */
import type { AbilityDef, AbilityId, BindKey, ClassDef, Command, DeepReadonly, HotbarKey, UnitId, Vec2, World } from '@keystone/sim';
import { HOTBAR_KEYS } from '@keystone/sim';

/**
 * Client-side binds = every hotbar key, in display order: 1–7 rotation, Q / E cooldowns, R, F, G.
 * 5, 6, 7 and G come from `ClassDef.extraBinds` (rotation rework F1); G falls back to the class's
 * first extra ability (Grip, Charge, Freedom) for content that predates `extraBinds`.
 */
export type UiBind = HotbarKey;
export const UI_BIND_KEYS: readonly UiBind[] = HOTBAR_KEYS;

/** Ability on a client bind (the BASE id: a transformed button still sends it, the sim resolves the form). */
export function abilityForBind(cls: Pick<ClassDef, 'binds' | 'extraAbilities' | 'extraBinds'> | undefined, bind: UiBind): AbilityId | undefined {
  if (!cls) return undefined;
  switch (bind) {
    case '5':
    case '6':
    case '7': // +basekits: the base kits' level-7 key
      return cls.extraBinds?.[bind];
    case 'G':
      // Content with extraBinds owns G outright (Stormcaller leaves it empty on purpose; its
      // extraAbilities hold transform forms such as Tempest, which must not leak onto G).
      return cls.extraBinds ? cls.extraBinds.G : cls.extraAbilities?.[0];
    default:
      return cls.binds[bind as BindKey];
  }
}

/** Held movement keys. 'turnLeft' / 'turnRight': the optional turn keys (Controls settings; unbound by default). */
export type MoveKey = 'forward' | 'back' | 'left' | 'right' | 'turnLeft' | 'turnRight';

export type InputAction =
  | { kind: 'move'; key: MoveKey }
  | { kind: 'ability'; bind: UiBind }
  | { kind: 'tabTarget'; reverse: boolean }
  | { kind: 'partyTarget'; slot: number }
  | { kind: 'dodge' }
  /** +jump: Space. */
  | { kind: 'jump' }
  | { kind: 'autorun' }
  | { kind: 'escape' };

/** Default keymap by KeyboardEvent.code (layout-independent). */
export const DEFAULT_KEYMAP: Readonly<Record<string, InputAction>> = {
  KeyW: { kind: 'move', key: 'forward' },
  KeyS: { kind: 'move', key: 'back' },
  KeyA: { kind: 'move', key: 'left' },
  KeyD: { kind: 'move', key: 'right' },
  ArrowUp: { kind: 'move', key: 'forward' },
  ArrowDown: { kind: 'move', key: 'back' },
  ArrowLeft: { kind: 'move', key: 'left' },
  ArrowRight: { kind: 'move', key: 'right' },
  Digit1: { kind: 'ability', bind: '1' },
  Digit2: { kind: 'ability', bind: '2' },
  Digit3: { kind: 'ability', bind: '3' },
  Digit4: { kind: 'ability', bind: '4' },
  Digit5: { kind: 'ability', bind: '5' },
  Digit6: { kind: 'ability', bind: '6' },
  Digit7: { kind: 'ability', bind: '7' },
  KeyQ: { kind: 'ability', bind: 'Q' },
  KeyE: { kind: 'ability', bind: 'E' },
  KeyR: { kind: 'ability', bind: 'R' },
  KeyF: { kind: 'ability', bind: 'F' },
  KeyG: { kind: 'ability', bind: 'G' },
  // +jump: Space jumps (WoW); Dodge moved to a Shift tap (input/dom.ts: Shift held with another key
  // or a click stays a modifier, so Shift+Tab / Shift+F / shift-click keep working).
  Space: { kind: 'jump' },
  ShiftLeft: { kind: 'dodge' },
  ShiftRight: { kind: 'dodge' },
  NumLock: { kind: 'autorun' },
  Tab: { kind: 'tabTarget', reverse: false },
  F1: { kind: 'partyTarget', slot: 0 },
  F2: { kind: 'partyTarget', slot: 1 },
  F3: { kind: 'partyTarget', slot: 2 },
  F4: { kind: 'partyTarget', slot: 3 },
  Escape: { kind: 'escape' },
};

export function mapKey(code: string, shift = false, keymap: Readonly<Record<string, InputAction>> = DEFAULT_KEYMAP): InputAction | null {
  const a = keymap[code];
  if (!a) return null;
  if (a.kind === 'tabTarget') return { kind: 'tabTarget', reverse: shift };
  // Shift+F is an alias for the extra F action (PLAN: "F | Taunt / Grip").
  if (shift && a.kind === 'ability' && a.bind === 'F') return { kind: 'ability', bind: 'G' };
  return a;
}

/**
 * Camera-relative movement direction in sim coordinates (unit vector or zero).
 * `cameraYaw` is the ground direction the camera looks (0 = +x, CCW positive).
 */
export function moveDirection(held: ReadonlySet<MoveKey>, cameraYaw: number): Vec2 {
  const f = (held.has('forward') ? 1 : 0) - (held.has('back') ? 1 : 0);
  const r = (held.has('right') ? 1 : 0) - (held.has('left') ? 1 : 0);
  if (f === 0 && r === 0) return { x: 0, y: 0 };
  const fwd = { x: Math.cos(cameraYaw), y: Math.sin(cameraYaw) };
  const right = { x: Math.sin(cameraYaw), y: -Math.cos(cameraYaw) };
  const x = fwd.x * f + right.x * r;
  const y = fwd.y * f + right.y * r;
  const len = Math.hypot(x, y);
  return { x: round(x / len), y: round(y / len) };
}

/**
 * Where a dodge lands: `distance` yards along the held move direction, or straight ahead
 * (facing) when no move key is held. The sim clamps it to walkable ground.
 */
export function dodgePoint(pos: Vec2, facing: number, moveDir: Vec2, distance: number): Vec2 {
  const moving = moveDir.x !== 0 || moveDir.y !== 0;
  const dx = moving ? moveDir.x : Math.cos(facing);
  const dy = moving ? moveDir.y : Math.sin(facing);
  return { x: pos.x + dx * distance, y: pos.y + dy * distance };
}

/** Snap float noise (cos(π/2) ≈ 6e-17, -0) so equal directions compare equal. */
const round = (v: number): number => Math.round(v * 1e6) / 1e6 || 0;

export const TAB_TARGET_RANGE = 45;

/**
 * Tab targeting: alive enemies within range, ordered by distance (ties by id).
 * Returns the next one after `currentId` (wrapping), or the nearest if none selected.
 */
export function nextTabTarget(world: DeepReadonly<World>, selfId: UnitId, currentId: UnitId | null, reverse = false, range = TAB_TARGET_RANGE): UnitId | null {
  const self = world.units[selfId];
  if (!self) return null;
  const cands = world.unitOrder
    .map((id) => world.units[id])
    .filter((u): u is NonNullable<typeof u> => !!u && u.alive && u.faction !== self.faction)
    .map((u) => ({ id: u.id, d: Math.hypot(u.pos.x - self.pos.x, u.pos.y - self.pos.y) }))
    .filter((c) => c.d <= range)
    .sort((a, b) => a.d - b.d || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  if (cands.length === 0) return null;
  const idx = currentId ? cands.findIndex((c) => c.id === currentId) : -1;
  if (idx < 0) return cands[0]!.id;
  const n = cands.length;
  return cands[(idx + (reverse ? n - 1 : 1)) % n]!.id;
}

export interface CastInputContext {
  actorId: UnitId;
  /** Currently selected target. */
  selectedId: UnitId | null;
  /** Unit under the cursor (mouse-over casting). */
  hoverId: UnitId | null;
  /** Ground point under the cursor. */
  cursorPoint: Vec2 | null;
  /** Use the hovered unit for ally / 'any' abilities when valid (WoW default: friendly only). */
  mouseover: boolean;
  /** Opt-in: also use the hovered unit for enemy abilities. */
  mouseoverEnemies?: boolean;
}

/**
 * Build the cast command for an ability. Ally spells fall back to self (WoW auto-self-cast),
 * ground spells use the cursor point, untargeted spells send no target.
 */
export function buildCastCommand(world: DeepReadonly<World>, ability: Pick<AbilityDef, 'id' | 'targeting'>, ctx: CastInputContext): Command {
  const base = { type: 'cast' as const, actorId: ctx.actorId, abilityId: ability.id };
  const self = world.units[ctx.actorId];
  const isAlly = (id: UnitId | null): id is UnitId => !!id && !!self && world.units[id]?.faction === self.faction;
  const isEnemy = (id: UnitId | null): id is UnitId => !!id && !!self && !!world.units[id] && world.units[id]!.faction !== self.faction;
  switch (ability.targeting) {
    case 'ally': {
      const hover = ctx.mouseover && isAlly(ctx.hoverId) && world.units[ctx.hoverId]?.alive ? ctx.hoverId : null;
      return { ...base, targetId: hover ?? (isAlly(ctx.selectedId) ? ctx.selectedId : ctx.actorId) };
    }
    case 'any': {
      // Heal-or-damage spells: mouse-over unit, then selected unit, then self.
      const hover = ctx.mouseover && ctx.hoverId && world.units[ctx.hoverId]?.alive ? ctx.hoverId : null;
      const sel = ctx.selectedId && world.units[ctx.selectedId] ? ctx.selectedId : null;
      return { ...base, targetId: hover ?? sel ?? ctx.actorId };
    }
    case 'enemy': {
      const hover = ctx.mouseover && ctx.mouseoverEnemies && isEnemy(ctx.hoverId) && world.units[ctx.hoverId]?.alive ? ctx.hoverId : null;
      const t = hover ?? (isEnemy(ctx.selectedId) ? ctx.selectedId : null);
      return t ? { ...base, targetId: t } : base;
    }
    case 'ground': {
      const p = ctx.cursorPoint ?? (self ? { x: self.pos.x, y: self.pos.y } : null);
      return p ? { ...base, point: p } : base;
    }
    default:
      return base;
  }
}

/** Facing (radians) from a to b. */
export const angleTo = (a: Vec2, b: Vec2): number => Math.atan2(b.y - a.y, b.x - a.x);

/** Smallest absolute difference between two angles. */
export function angleDiff(a: number, b: number): number {
  return Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
}
